// Nomad-mon Go! — SC2-B Phase 1: player identity, persistent collection,
// pack economy. Owns the current player (localStorage 'nomadmon-player'),
// the server state cache, and the GUEST fallback — the same economy rules
// simulated entirely client-side. Server errors / offline degrade to
// guest-style local behavior for the session: play is never blocked and
// nothing is queued for later replay.

import { engineHeaders, isUpdateRequired, forceUpdate } from './update-required.js';
import { MAX_XP, levelFromXp, victoryXp } from './levelling.js';
import { recipe as forgedRecipe, dupeXpFor, checkReq, mintXp } from './forged.js';

const API = '/api/nomadmon';
const K_PLAYER = 'nomadmon-player';
const K_ECON = 'nomadmon-guest-econ';
const K_COLL = 'nomadmon-guest-collection';
const K_REC = 'nomadmon-guest-record';
const K_PROGRESS = 'nomadmon-guest-card-progress-v1';
const K_DEVICES = 'nomadmon-device-tokens-v1';
const K_FIELD = 'nomadmon-fielded-history-v1';
const DECK_KEY = 'nomadmon-deck-v1';       // migration: seed guest ownership
const BONUS_START = 6, DAILY_PACKS = 3;

let player = null;         // stored name, 'GUEST' sentinel, or null (unpicked)
let srv = null;            // last server state payload for the signed-in player
let offline = false;       // a player call failed -> guest-style session
let gcoll = null;          // guest collection cache {id: count}, write-through
let collMap = null;        // server collection lookup, derived from srv
let progressMap = null;
let capabilities = {};
let pendingDeviceClaim = (() => {
  try { return new URLSearchParams(location.search).get('claim') || null; }
  catch { return null; }
})();

function lsGet(k, fb) {
  try { const v = JSON.parse(localStorage.getItem(k)); return v ?? fb; }
  catch { return fb; }
}
function lsSet(k, v) {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ }
}

function currentDeviceToken() {
  if (!player || player === 'GUEST') return null;
  const tokens = lsGet(K_DEVICES, {});
  return tokens[String(player).toLowerCase()] || null;
}
function rememberDeviceToken(token) {
  if (!player || player === 'GUEST' || !token) return;
  const tokens = lsGet(K_DEVICES, {});
  tokens[String(player).toLowerCase()] = token;
  lsSet(K_DEVICES, tokens);
}

async function api(path, body) {
  const token = currentDeviceToken();
  const headers = body ? { 'Content-Type': 'application/json', ...engineHeaders() } : { ...engineHeaders() };
  if (token) headers.Authorization = `Bearer ${token}`;
  const r = await fetch(API + path, {
    method: body ? 'POST' : 'GET',
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal: AbortSignal.timeout(6000),
  });
  // A body that never parses is a FAILED call, not an empty success: when the
  // 6 s abort lands mid-body (a main thread stalled by a busy first load),
  // json() rejects, and returning {} here used to install an EMPTY state — a
  // signed-in player then saw no cards for the whole session.
  const j = await r.json().catch(() => null);
  if (!r.ok) {
    const b = j || {};
    if (isUpdateRequired(r.status, b)) forceUpdate();
    const e = new Error(b.error || `HTTP ${r.status}`); e.status = r.status; e.body = b; throw e;
  }
  if (j === null) { const e = new Error('Unreadable response'); e.status = r.status; throw e; }
  return j;
}

async function usePendingDeviceClaim(name) {
  if (!capabilities.deviceClaims || !pendingDeviceClaim || currentDeviceToken()) return null;
  const claimed = await api('/device/claim', { name, code: pendingDeviceClaim });
  rememberDeviceToken(claimed.deviceToken);
  pendingDeviceClaim = null;
  try {
    const url = new URL(location.href);
    url.searchParams.delete('claim');
    history.replaceState(history.state, '', url);
  } catch { /* URL cleanup is cosmetic */ }
  return claimed;
}

function setSrv(s) { srv = s; collMap = null; progressMap = null; }
function srvColl() {
  if (!collMap) {
    collMap = {};
    for (const c of (srv && srv.collection) || []) collMap[c.id] = c.count;
  }
  return collMap;
}
function localProgressKey() {
  return isGuest() ? K_PROGRESS : `${K_PROGRESS}:${String(player || 'offline').toLowerCase()}`;
}
function guestProgress() { return lsGet(localProgressKey(), {}); }
function srvProgress() {
  if (!progressMap) {
    progressMap = {};
    for (const c of (srv && srv.collection) || []) {
      const xp = Math.max(0, Math.min(MAX_XP, Number(c.xp) || 0));
      progressMap[c.id] = { xp, level: levelFromXp(xp), holo: c.holo === true };
    }
  }
  return progressMap;
}

// ---------------------------------------------------------------- guest economy
// Same rules as the server: bonus pool starts at 6, 3 dailies per UTC day
// (non-rollover), dailies spent first.
function utcDay() { return new Date().toISOString().slice(0, 10); }
function nextUtcMidnight() {
  const d = new Date();
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
}
function guestEcon() {
  const e = lsGet(K_ECON, null) || { bonusLeft: BONUS_START, dailyLeft: DAILY_PACKS, day: utcDay() };
  if (e.day !== utcDay()) { e.dailyLeft = DAILY_PACKS; e.day = utcDay(); lsSet(K_ECON, e); }
  return e;
}
function guestColl() {
  if (!gcoll) gcoll = lsGet(K_COLL, {});
  return gcoll;
}
function guestRec() { return lsGet(K_REC, { wins: 0, losses: 0 }); }

// localMode: the guest data drives everything — either an actual guest, or a
// signed-in player whose server is unreachable (guest-style session fallback).
function localMode() { return isGuest() || offline || !srv; }

// ---------------------------------------------------------------- API surface
// init(): load the stored player and run the GUEST deck migration — any card
// ids already in the stored deck are seeded into the guest collection at
// count 1 so decks built before ownership existed keep working.
export function init() {
  try { player = localStorage.getItem(K_PLAYER) || null; } catch { player = null; }
  const sel = lsGet(DECK_KEY, null);
  if (sel) {
    const coll = guestColl();
    let dirty = false;
    for (const id of [...(sel.chars || []), ...(sel.supports || [])]) {
      if (!coll[id]) { coll[id] = 1; dirty = true; }
    }
    if (dirty) lsSet(K_COLL, coll);
  }
}

// ------------------------------------------------------- SC18 FIRST CLAIMS
// The server records the FIRST player ever to pull each mythic / alt card.
// This is PRESTIGE, not exclusivity: everyone can still pull the card, the
// registry only ever remembers who got there first. Cached in memory for the
// session and refreshed whenever a pack lands.
//
// Honest limitation, by design: guests and offline sessions roll their packs
// entirely client-side and never reach the server, so a guest pulling a mythic
// does NOT claim it. The engraving is only ever truthful for signed-in pulls,
// and the client never fabricates one locally.
let firsts = {};           // { cardId: { player, ts, mine } }

export function firstClaims() { return firsts; }

export async function fetchFirstClaims() {
  try {
    const j = await api('/firsts');
    const map = {};
    for (const r of j.firsts || []) {
      map[r.cardId] = { player: r.player, ts: r.claimedTs, mine: r.player === currentPlayer() };
    }
    firsts = map;
  } catch { /* offline: the aftermath simply shows no engraving */ }
  return firsts;
}

export function currentPlayer() { return player && player !== 'GUEST' ? player : null; }
export function isGuest() { return !currentPlayer(); }
export function hasIdentity() { return player != null; }
export function setCapabilities(value) { capabilities = value && typeof value === 'object' ? value : {}; }
export function canFeedDuplicates() {
  return !!capabilities.duplicatesFeed && !isGuest() && !offline && !!srv && !!currentDeviceToken();
}
export function canTradeSpares() {
  return !!capabilities.spareTrading && !isGuest() && !offline && !!srv && !!currentDeviceToken();
}
export function canUseWhatsAppChallenges() {
  return !!capabilities.whatsappChallenges && !isGuest() && !offline && !!srv && !!currentDeviceToken();
}

// WhatsApp never accepts a display name as proof of identity. A named,
// device-claimed game session mints a short-lived one-time code; the bot binds
// the exact sender JID that redeems it. The code is intentionally obtained in
// game so somebody in the group cannot claim another player's name.
export async function createWhatsAppLinkCode() {
  if (!canUseWhatsAppChallenges()) {
    throw new Error('Claim this player on this device before linking WhatsApp.');
  }
  return api('/whatsapp/link-code', {});
}

// The deck picker is local-first, but group challenges resolve on the server.
// Keep a server copy after every edit so the briefing can truthfully describe
// the same current deck. Partial decks are stored too; the challenge service
// replaces an illegal 5+2 selection with a policy-filtered seeded deck.
export async function syncWhatsAppDeck(deck) {
  if (!canUseWhatsAppChallenges()) return false;
  try {
    await api('/whatsapp/deck', {
      chars: Array.isArray(deck?.chars) ? deck.chars.slice(0, 5) : [],
      supports: Array.isArray(deck?.supports) ? deck.supports.slice(0, 2) : [],
    });
    return true;
  } catch { return false; }
}

export async function fetchPlayers() {
  const j = await api('/players');
  return j.players || [];
}

// selectPlayer: friends-trust claim (no auth). 'GUEST' picks the local-only
// path; a failed server claim still stores the name and plays on guest-style.
export async function selectPlayer(name) {
  if (name === 'GUEST') {
    player = 'GUEST';
    setSrv(null); offline = false;
    try { localStorage.setItem(K_PLAYER, 'GUEST'); } catch { /* private */ }
    return null;
  }
  player = name;
  try { localStorage.setItem(K_PLAYER, name); } catch { /* private */ }
  try {
    let selected = await api('/player/select', { name });
    try { selected = (await usePendingDeviceClaim(name)) || selected; }
    catch { /* wrong/used claim leaves normal solo identity intact */ }
    setSrv(selected);
    offline = false;
  } catch { setSrv(null); offline = true; }
  return srv;
}

export async function refreshState() {
  if (isGuest()) return null;
  try {
    let refreshed = await api(`/state/${encodeURIComponent(player)}`);
    try { refreshed = (await usePendingDeviceClaim(player)) || refreshed; }
    catch { /* claim failure never blocks ordinary solo state */ }
    setSrv(refreshed);
    offline = false;
  } catch { offline = true; }
  return srv;
}

export function state() { return srv; }

// COMBAT V3 softlock fix: every player owns these two support cards from day
// one (a heal and a hit), so a fresh collection can always field a legal deck.
// SERVER MUST MATCH: whatsapp-poster's deck/ownership checks treat them as owned.
export const STARTER_SUPPORTS = Object.freeze(['rocket-snack', '16-2-vs-15-1-the-mechanical-honesty-war']);
export function ownedCount(id) {
  const n = localMode() ? (guestColl()[id] || 0) : (srvColl()[id] || 0);
  return n || (STARTER_SUPPORTS.includes(id) ? 1 : 0);
}
export function cardProgress(id) {
  const p = localMode() ? guestProgress()[id] : srvProgress()[id];
  const xp = Math.max(0, Math.min(MAX_XP, Number(p && p.xp) || 0));
  return { xp, level: levelFromXp(xp) };
}
export function cardLevel(id) { return cardProgress(id).level; }
// v10 HOLO: the server's persisted flag (signed-in players; it never clears).
// Guests and level-based Holo are derived by levelling.isHolo(card, level).
export function cardHoloFlag(id) { return !localMode() && !!(srvProgress()[id] && srvProgress()[id].holo); }
// MP deck posts carry the device token so the server can confirm Holo claims.
export function authHeader() { const t = currentDeviceToken(); return t ? { Authorization: `Bearer ${t}` } : {}; }
export function cardLevels(ids) {
  return Object.fromEntries((ids || []).map(id => [id, cardLevel(id)]));
}

// LUCKY DRAW heat history. The current server state has no field for this, so
// both guests and named players use identity-scoped local persistence. Keeping
// the player name in the key prevents one collection's heat from affecting
// another player on a shared device.
function fieldKey() {
  return `${K_FIELD}:${String(currentPlayer() || 'guest').trim().toLowerCase()}`;
}
export function fieldHistory() {
  const raw = lsGet(fieldKey(), {});
  const fielded = raw.fielded && typeof raw.fielded === 'object' ? raw.fielded : {};
  const recent = Array.isArray(raw.recent) ? raw.recent.slice(0, 3)
    .map(row => Array.isArray(row) ? [...new Set(row)].slice(0, 5) : []) : [];
  return { fielded, recent };
}
export function recordFielded(ids) {
  const clean = [...new Set((ids || []).filter(id => typeof id === 'string'))].slice(0, 5);
  if (!clean.length) return fieldHistory();
  const h = fieldHistory();
  const now = Date.now();
  for (const id of clean) h.fielded[id] = { count: (h.fielded[id]?.count || 0) + 1, last: now };
  h.recent.unshift(clean);
  h.recent = h.recent.slice(0, 3);
  lsSet(fieldKey(), h);
  return h;
}

// ---------------------------------------------------------------------------
// GUEST SANDBOX (SC26) — explicit, named, and reversible.
//
// Jack asked for this and it stays: as GUEST the pack economy is a sandbox of
// 50 packs that never decrement, so testing never runs dry.
//
// SC30 — Jack's ruling: the sandbox is PERMANENT and SHIPS ON. GUEST is the
// testing sandbox and keeps its 50 non-decrementing packs for good. Do not
// "fix" this and do not turn it off for a release; earlier notes here and in
// docs/SMOKE_BASELINE.md said it had to be off to ship, and that is superseded.
//
// It only ever applies to GUEST. Every branch below is gated on
// `isGuest() && guestSandboxOn()`, so a NAMED player (Jack/Ron/Alex/Cerj/Ajay
// or any future name) is untouched: they run the real server economy — 6 signup
// bonus + 3/day non-rollover, dailies spent first, 402 when empty.
//
// While the sandbox is ON the zero-credit state is unreachable FOR GUEST by
// construction, so any test of the empty-wallet CTA taken as guest is
// meaningless — `edge-credits` turns it OFF for exactly that reason.
//
// Turn it off / on:
//     ?sandbox=off   ?sandbox=on          URL wins, and persists the choice
//     localStorage['nomadmon-guest-sandbox'] = 'off' | 'on'
// Default when nothing says otherwise: ON.
// ---------------------------------------------------------------------------
const GUEST_SANDBOX_CREDITS = 50;
const K_SANDBOX = 'nomadmon-guest-sandbox';

export function guestSandboxOn() {
  try {
    const q = new URLSearchParams(location.search).get('sandbox');
    if (q === 'off' || q === 'on') {
      try { localStorage.setItem(K_SANDBOX, q); } catch { /* private mode */ }
      return q === 'on';
    }
    return localStorage.getItem(K_SANDBOX) !== 'off';
  } catch { return true; }          // no DOM/storage: keep the sandbox on
}

export function creditsLeft() {
  if (isGuest() && guestSandboxOn()) return GUEST_SANDBOX_CREDITS;
  if (localMode()) { const e = guestEcon(); return e.dailyLeft + e.bonusLeft; }
  return srv.creditsLeft ?? ((srv.dailyLeft || 0) + (srv.bonusLeft || 0));
}

export function nextGrantUtc() {
  if (localMode()) return nextUtcMidnight();
  return srv.nextGrantUtc;
}

// SC4 GOLD cadence: every 3rd pack is GOLD. Server-authoritative for named
// players (packs_opened counter); the guest path counts locally in K_ECON.
const GOLD_EVERY = 3;

// nextPackGold(): is the NEXT pack the player opens a GOLD pack? Drives the
// pack button's anticipation label.
export function nextPackGold() {
  if (localMode()) return ((guestEcon().opened || 0) + 1) % GOLD_EVERY === 0;
  return !!srv.nextPackGold;
}

// SC7: lifetime packs opened — the battle-gate currency. Server truth for
// named players (/state packsOpened); the guest path counts locally in K_ECON.
// A missing server field reads as veteran (never lock an existing player).
export function packsOpened() {
  if (localMode()) return guestEcon().opened || 0;
  return srv.packsOpened ?? 999;
}

// openPack(): spend one credit. Resolves { ok, cards|null, gold, nextGrantUtc? }.
// cards = the server's prescribed [{id,rarity} x5, x6 gold] which REPLACE the
// local roll; null = roll locally (guest path, keeps the seeded pack.js draw).
export async function openPack() {
  if (!localMode()) {
    try {
      const j = await api('/pack/open', { player });
      setSrv({ ...srv, ...j, collection: srv.collection });
      // SC18: the server answers with any cards THIS pull claimed first —
      // fold them straight into the cache so the ceremony can engrave the
      // card without a second round trip.
      for (const id of j.firsts || []) firsts[id] = { player, ts: Date.now(), mine: true };
      // v11 DUPE -> XP: the server already converted any duplicate into XP
      // for the owned card; remember how, so the reveal can show it.
      serverPackCards = Array.isArray(j.cards) ? j.cards : null;
      return { ok: true, cards: j.cards, gold: !!j.gold };
    } catch (e) {
      if (e.status === 402) return { ok: false, nextGrantUtc: e.body && e.body.nextGrantUtc };
      offline = true;                       // degrade to local for the session
    }
  }
  const econ = guestEcon();
  // GUEST is the testing sandbox: unlimited packs, no economy. A signed-in
  // player degraded to local mode still spends normally — and so does a guest
  // once the sandbox is switched off (see guestSandboxOn above).
  if (!isGuest() || !guestSandboxOn()) {
    if (econ.dailyLeft + econ.bonusLeft <= 0) return { ok: false, nextGrantUtc: nextUtcMidnight() };
    if (econ.dailyLeft > 0) econ.dailyLeft--; else econ.bonusLeft--; // dailies first
  }
  econ.opened = (econ.opened || 0) + 1;     // lifetime counter -> gold cadence
  lsSet(K_ECON, econ);
  return { ok: true, cards: null, gold: econ.opened % GOLD_EVERY === 0 };
}

// notePackContents(cards|ids): keep ownership fresh right after a pack draws.
// v11 DUPE -> XP: a card you already own is NOT stacked as a spare any more —
// the duplicate becomes XP for the card you have (data/fusions.json dupeXp:
// common 150 ~ three NORMAL wins). A MAXED card keeps the copy as a spare.
// The server decides this for signed-in players (j.cards[i].dupe); the guest
// path applies the same rule locally. packDupe(id) feeds the reveal.
let packDupes = {};
let serverPackCards = null;
export function packDupe(id) { return packDupes[id] || null; }
function dupeRow(before, gained) {
  const after = Math.min(MAX_XP, before + gained);
  return { xpBefore: before, xpAfter: after, gained: after - before,
    levelBefore: levelFromXp(before), levelAfter: levelFromXp(after) };
}
export function notePackContents(list) {
  const cards = (list || []).map(x => (typeof x === 'string' ? { id: x } : x));
  packDupes = {};
  if (localMode()) {
    const coll = guestColl();
    const prog = guestProgress();
    for (const c of cards) {
      if ((coll[c.id] || 0) > 0) {
        const before = Math.max(0, Math.min(MAX_XP, Number(prog[c.id] && prog[c.id].xp) || 0));
        if (before >= MAX_XP) { coll[c.id] = (coll[c.id] || 0) + 1; packDupes[c.id] = { maxed: true }; continue; }
        const row = dupeRow(before, dupeXpFor(c.rarity || 'common'));
        prog[c.id] = { xp: row.xpAfter, level: row.levelAfter };
        packDupes[c.id] = row;
      } else coll[c.id] = 1;
    }
    lsSet(K_COLL, coll);
    lsSet(localProgressKey(), prog);
  } else {
    const map = srvColl();
    const byServer = Object.fromEntries((serverPackCards || []).map(c => [c.id, c]));
    for (const c of cards) {
      const dupe = byServer[c.id] && byServer[c.id].dupe;
      if (dupe && !dupe.maxed) {
        packDupes[c.id] = { ...dupe, gained: dupe.gained ?? (dupe.xpAfter - dupe.xpBefore) };
        const row = (srv.collection || []).find(r => r.id === c.id);
        if (row) { row.xp = dupe.xpAfter; row.level = dupe.levelAfter; if (dupe.xpAfter >= 680) row.holo = true; }
        progressMap = null;
      } else {
        if (dupe && dupe.maxed) packDupes[c.id] = { maxed: true };
        map[c.id] = (map[c.id] || 0) + 1;
        if (!dupe) {
          const row = (srv.collection || []).find(r => r.id === c.id);
          if (row) row.count = map[c.id]; else (srv.collection ||= []).push({ id: c.id, count: 1, xp: 0, level: 1 });
        }
      }
    }
    serverPackCards = null;
  }
  return packDupes;
}

// ---------------------------------------------------------------- v11 FORGE
// Duos and Fusions. Signed-in players: the SERVER validates, spends the cards
// and mints (one atomic transaction); the client only renders the result.
// Guests: the same rules run locally (the GUEST sandbox). Offline signed-in
// players cannot forge — spending cards is never queued.
const K_FORGED = 'nomadmon-guest-forged-v1';
function idemKey() {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
}
export function forgedOwned(id) { return ownedCount(id) > 0; }
export function forgedList() {
  if (localMode()) return lsGet(K_FORGED, []);
  return (srv && srv.forged) || [];
}
export function cardCount(id) { return localMode() ? (guestColl()[id] || 0) : (srvColl()[id] || 0); }
export async function forge(kind, recipeId, reagents, byId) {
  const r = forgedRecipe(recipeId);
  if (!r || r.kind !== kind) return { ok: false, code: 'UNKNOWN_RECIPE', error: 'Unknown recipe.' };
  const spend = kind === 'duo' ? r.sources.slice() : [...new Set(reagents || [])];
  if (isGuest()) return forgeLocal(kind, r, spend, byId);
  if (offline || !srv) return { ok: false, code: 'OFFLINE', error: 'Forging needs a connection.' };
  try {
    const body = kind === 'duo' ? { player, duoId: recipeId, idempotencyKey: idemKey() }
      : { player, fusionId: recipeId, reagents: spend, idempotencyKey: idemKey() };
    const j = await api(`/forge/${kind}`, body);
    if (j.state) setSrv(j.state);
    return { ok: true, id: r.id, xp: j.xp, level: j.level, spent: j.spent || [] };
  } catch (e) {
    return { ok: false, code: (e.body && e.body.code) || 'FORGE_FAILED', error: e.message, missing: e.body && e.body.missing };
  }
}
function forgeLocal(kind, r, spend, byId) {
  const coll = guestColl();
  const prog = guestProgress();
  if ((coll[r.id] || 0) > 0) return { ok: false, code: 'ALREADY_FORGED', error: 'Already forged.' };
  const missing = spend.filter(id => !(coll[id] > 0) || !byId[id] || byId[id].forged);
  if (missing.length) return { ok: false, code: 'MISSING_CARD', error: 'Missing cards.', missing };
  if (kind === 'fusion') {
    const chk = checkReq(r.req, spend.map(id => byId[id]));
    if (!chk.ok) return { ok: false, code: 'BAD_REAGENTS', error: chk.problems.join('; ') };
  }
  const contrib = [], spent = [];
  for (const id of spend) {
    if (coll[id] > 1) { coll[id]--; contrib.push(0); spent.push({ id, removed: false, xp: 0 }); continue; }
    const xp = Math.max(0, Number(prog[id] && prog[id].xp) || 0);
    delete coll[id]; delete prog[id];
    contrib.push(xp); spent.push({ id, removed: true, xp });
  }
  const xp = mintXp(contrib, MAX_XP);
  coll[r.id] = 1;
  prog[r.id] = { xp, level: levelFromXp(xp) };
  lsSet(K_COLL, coll);
  lsSet(localProgressKey(), prog);
  const list = lsGet(K_FORGED, []);
  list.push({ id: r.id, kind, mintedTs: Date.now(), startXp: xp });
  lsSet(K_FORGED, list);
  return { ok: true, id: r.id, xp, level: levelFromXp(xp), spent };
}

// Duplicate training never falls back or queues. The quote and mutation are
// both server decisions bound to this device credential; presentation only
// renders the returned labels, ratios, and before/after values.
export async function duplicateFeedQuote(cardId) {
  if (!canFeedDuplicates()) return { visible: false, canFeed: false };
  return api(`/feed/quote/${encodeURIComponent(cardId)}`);
}

export async function feedDuplicate(cardId, idempotencyKey) {
  if (!canFeedDuplicates()) throw new Error('Duplicate training is unavailable.');
  const result = await api('/feed', { cardId, count: 1, idempotencyKey });
  if (srv && Array.isArray(srv.collection)) {
    const row = srv.collection.find(c => c.id === cardId);
    if (row) {
      row.count = result.countAfter;
      row.xp = result.xpAfter;
      row.level = result.levelAfter;
    }
    collMap = null;
    progressMap = null;
  }
  return result;
}

// Spare trading is always server-authoritative and never falls back offline.
// Options are already filtered for identity, age/packs, reservations, cooldown,
// rarity bands, pair caps, player caps, and device caps. The client only paints
// the returned facts and submits the selected IDs.
export async function spareTradeOptions(cardId) {
  if (!canTradeSpares()) return { visible: false, canTrade: false, friends: [] };
  return api(`/trades/options/${encodeURIComponent(cardId)}`);
}
export async function createSpareTrade(recipient, offeredCardId, requestedCardId, idempotencyKey) {
  if (!canTradeSpares()) throw new Error('Spare trading is unavailable.');
  return api('/trades', { recipient, offeredCardId, requestedCardId, idempotencyKey });
}
export async function tradeInbox() {
  if (!canTradeSpares()) return { incoming: [], outgoing: [], ceremonies: [], notices: [], badgeCount: 0 };
  return api('/trades');
}
export async function acceptTrade(tradeId, idempotencyKey) {
  const result = await api(`/trades/${encodeURIComponent(tradeId)}/accept`, { idempotencyKey });
  await refreshState();
  return result;
}
export async function declineTrade(tradeId, idempotencyKey) {
  return api(`/trades/${encodeURIComponent(tradeId)}/decline`, { idempotencyKey });
}
export async function cancelTrade(tradeId, idempotencyKey) {
  return api(`/trades/${encodeURIComponent(tradeId)}/cancel`, { idempotencyKey });
}
export async function acknowledgeTradeCeremony(eventId, idempotencyKey) {
  return api(`/social/events/${encodeURIComponent(eventId)}/ceremony`, { idempotencyKey });
}

export function record() {
  if (localMode()) return guestRec();
  return srv.record || { wins: 0, losses: 0 };
}

// reportRecord('win'|'loss', meta): server for players, localStorage tally for
// the guest-style path. A failed server report is dropped, never queued.
// SC32: meta carries { bracket, difficulty } so the SERVER can pay the battle
// reward. The client never asserts how many packs it earned — it only reports
// what happened and displays what the server paid.
export async function reportRecord(result, meta = {}) {
  if (localMode()) {
    const r = guestRec();
    if (result === 'win') r.wins++; else r.losses++;
    lsSet(K_REC, r);
    const changed = [];
    if (result === 'win' && Array.isArray(meta.cards)) {
      const all = guestProgress();
      const award = victoryXp(meta.enemyPower, meta.difficulty, meta.mode);
      for (const id of [...new Set(meta.cards)].slice(0, 5)) {
        if (!guestColl()[id]) continue;
        const before = Math.max(0, Number(all[id] && all[id].xp) || 0);
        const xp = Math.min(MAX_XP, before + award);
        const row = { xp, level: levelFromXp(xp) };
        all[id] = row;
        changed.push({ id, beforeLevel: levelFromXp(before), ...row, gained: xp - before });
      }
      lsSet(localProgressKey(), all);
    }
    return { record: r, progress: changed };
  }
  try {
    const j = await api('/record', {
      player, result,
      bracket: meta.bracket || 'even',
      difficulty: meta.difficulty || 'normal',
      cards: Array.isArray(meta.cards) ? meta.cards.slice(0, 5) : [],
      enemyPower: Number(meta.enemyPower) || 0,
      mode: meta.mode === 'mp' ? 'mp' : 'solo',
    });
    if (srv) {
      srv.record = { wins: j.wins, losses: j.losses };
      srv.winStreak = j.streak;
      if (typeof j.creditsLeft === 'number') srv.creditsLeft = j.creditsLeft;
      if (typeof j.bonusLeft === 'number') srv.bonusLeft = j.bonusLeft;
      srv.lastReward = j.reward || null;
      if (Array.isArray(j.progress)) {
        const map = srvProgress();
        for (const p of j.progress) map[p.id] = { xp: p.xp, level: p.level };
      }
    }
    return { record: record(), progress: j.progress || [] };
  } catch { return { record: record(), progress: [] }; }
}

// ---------------------------------------------------------------- SC33 gauntlet
// The BOSS GAUNTLET clear mark. Server-authoritative for named players: the
// client says "I cleared it", the server decides whether that is the FIRST
// clear and therefore whether it pays. Guests keep a local latch and a local
// pack grant, exactly as they do for every other economy action.
const K_GAUNT = 'nomadmon-gauntlet-cleared';
export function gauntletCleared() {
  if (!localMode()) return !!(srv && srv.gauntletCleared);
  try { return localStorage.getItem(K_GAUNT) === '1'; } catch { return false; }
}
export async function reportGauntlet(packs = 5) {
  if (localMode()) {
    try { localStorage.setItem(K_GAUNT, '1'); } catch { /* private */ }
    // guests are paid locally — the same bonus pool the packs come out of
    const e = guestEcon();
    e.bonusLeft = (e.bonusLeft || 0) + packs;
    lsSet(K_ECON, e);
    return { gauntletCleared: true, reward: { packs } };
  }
  try {
    const j = await api('/gauntlet', { player });
    if (srv) {
      srv.gauntletCleared = true;
      if (typeof j.creditsLeft === 'number') srv.creditsLeft = j.creditsLeft;
      if (typeof j.bonusLeft === 'number') srv.bonusLeft = j.bonusLeft;
      if (j.reward) srv.lastReward = j.reward;
    }
    return j;
  } catch { return { gauntletCleared: true, reward: { packs: 0 } }; }
}

// The reward the SERVER paid for the last battle (null for guests / offline).
export function lastReward() { return (srv && srv.lastReward) || null; }
export function serverStreak() { return (srv && srv.winStreak) || 0; }
